export type ReferralFaqItem = {
  question: string;
  answer: string;
};

export const REFERRAL_FAQ_ITEMS: ReferralFaqItem[] = [
  {
    question: 'What is the BCC Referral Program?',
    answer:
      'The BCC Referral Program lets eligible BCC customers earn $200 in travel credits by referring friends and family who are new to BCC. When someone you refer submits a trip request through your personal referral link and completes their first booking, they receive $200 toward their trip at checkout. You receive your own $200 credit the day after they complete their first outbound departure.',
  },
  {
    question: 'How do I find my referral link?',
    answer:
      'Log in to the BCC customer portal, click your name in the top-right corner, and choose Referrals from the dropdown. Your unique referral link is displayed on that page. Copy it and share it however you prefer — email, WhatsApp, or any other channel. Your link looks like this: bcconsolidator.com/?ref=yourname-xxxxxx',
  },
  {
    question: 'Does my referral link expire?',
    answer:
      'No. Your referral link never expires and is permanently linked to your account. You can share it at any time. However, each person who signs up through your link has 90 days from their registration date to complete a booking for the credits to apply.',
  },
  {
    question: 'Who counts as a new customer I can refer?',
    answer:
      'You can refer anyone who has not flown with BCC in the last two years. This is checked automatically when they sign up (against their email and phone number) and again when they complete their booking (against the names and dates of birth of all passengers on their trip). If they — or all the passengers they add — have flown with us recently, the referral will be marked as Ineligible.',
  },
  {
    question: 'Can I refer a family member?',
    answer:
      'Yes, as long as that family member has not flown with BCC in the last two years and is genuinely booking as a new customer. The eligibility check is based on prior flight history, not on their relationship to you.',
  },
  {
    question: 'Can I refer someone who has a BCC account but has never flown?',
    answer:
      'Yes. The eligibility check is based on whether they have an outbound departure on record with us within the last two years — not on whether they have an account. If they have an account but no qualifying flight history, they can be a valid referee.',
  },
  {
    question: 'What happens after my friend submits their request through my link?',
    answer:
      'You will receive an email and an SMS from your BCC agent as soon as your friend submits a request. Your agent will also reach out to your friend to start preparing their trip options. You can track the status of the referral on your Referrals page in the portal at any time.',
  },
  {
    question: 'Will I always be notified when someone uses my link?',
    answer:
      'Yes. Every time someone submits a request through your referral link, you will receive both an email (from your BCC agent) and an SMS notification. This happens regardless of whether the referral turns out to be eligible or ineligible.',
  },
  {
    question: 'What does my referred friend receive?',
    answer:
      'Your referred friend receives a $200 BCC credit applied automatically at checkout when they complete their booking. This credit reduces the amount they need to pay. It is valid for 90 days from the date they submitted their trip request — not from the date they book, so they should be encouraged to book promptly.',
  },
  {
    question: 'How soon must my referred clients fly to be eligible?',
    answer:
      'They can fly any time they want. They need to complete their booking within 90 days from registering the trip request via your referral link.',
  },
  {
    question: 'My friend has never flown with BCC but they did not receive the credit at checkout.',
    answer:
      "There could be a few reasons why this happens. They may have missed completing the booking within the 90 day window or they may have not added themselves as passengers but instead added passengers who have previously flown with us. If neither are true, please contact your sales agent and we'll resolve it.",
  },
  {
    question: 'When do I receive my $200 credit?',
    answer:
      "Your $200 credit is added to your BCC wallet the day after your referred friend's first outbound departure. This is processed automatically overnight. You will not need to do anything — the credit will appear in your account automatically.",
  },
  {
    question: 'Why do I only get my credit after my friend flies, not when they book?',
    answer:
      'This ensures the trip has actually taken place before we issue the reward. It protects both parties and ensures the integrity of the program.',
  },
  {
    question: 'Can my friends use the credit for any trip with BCC?',
    answer:
      'At BCC, we facilitate international trips with business class cabin or higher. Your own sales agent will serve any trips matching this criteria.',
  },
  {
    question: 'How long is my $200 credit valid for?',
    answer: 'Your referral credit is valid for two years from the date it is issued.',
  },
  {
    question: 'Can I use my $200 credit on any booking?',
    answer:
      'Yes. Your referral credit is added to your BCC wallet and can be applied to any future booking through the customer portal. It is applied automatically at checkout alongside any other wallet balance you may have.',
  },
  {
    question: 'How do I track the status of my referrals?',
    answer:
      "Go to the Referrals page in your customer portal. Each referral shows the person's name (partially masked for privacy), the days remaining for them to book, and a current status. Statuses are: Referral submitted, Contacted, In progress, Booked, Success, Ineligible, and Expired. See the Terms & Conditions section below for a full description of each status.",
  },
  {
    question: "What does 'Ineligible' mean?",
    answer:
      'A referral is marked Ineligible when the automatic eligibility check determines that the referred person does not qualify for the program. This happens in two cases: (1) at sign-up, if their email or phone matches an existing BCC customer who has flown with us in the last two years; or (2) at checkout, if every passenger on their booking has flown with us in the last two years and no new traveller is present. No credits are issued to either party in this case.',
  },
  {
    question: "What does 'Expired' mean?",
    answer:
      'A referral expires when your referred friend does not complete a booking within 90 days of submitting their trip request. After expiry, no credits are issued. The referral still counts toward your 50-referral total.',
  },
  {
    question: 'Can I refer the same person twice?',
    answer:
      'No. Once a person has submitted a request through a referral link — regardless of the outcome — they cannot be referred again. If their first referral was ineligible or expired, resubmitting through your link will not create a new referral record.',
  },
  {
    question: 'Can two people refer the same friend?',
    answer:
      "No. Only the first referral link used by your friend at the time of their sign-up will be recorded. If your friend signs up through someone else's link first, that referral is theirs.",
  },
  {
    question: 'Is there a limit to how many people I can refer?',
    answer:
      "Yes. You can have a maximum of 50 referral records linked to your account. Once you reach 50, further sign-ups through your link will be accepted as normal enquiries but will not be linked to your referral account and no credits will be issued. We may extend this on a case by case basis so please speak to your sales agent if you've reached the 50 cap.",
  },
  {
    question: 'What happens if my friend cancels their booking?',
    answer:
      'If your friend cancels their booking after their $200 credit was applied at checkout, their credit is automatically removed and you will not get your referral credit.',
  },
  {
    question: 'What if my friend books multiple trips — do I earn $200 for each one?',
    answer:
      'No. You earn one $200 credit per referred person, issued after their first outbound departure with BCC. Subsequent trips made by the same person do not generate additional referral credits.',
  },
  {
    question: 'My referral is not showing up on my portal. What should I do?',
    answer:
      "If a referral does not appear within 24 hours of your friend signing up, please contact your BCC agent. Provide your friend's name and the approximate date they submitted their request. Our team will investigate and update your referral record if appropriate.",
  },
  {
    question: 'Which agent is assigned to my referred friend?',
    answer:
      'Your referred friend is automatically assigned to your current BCC agent. This is so that your agent — who already knows your travel preferences — can provide a consistent service experience to your friend. If your current agent is not available, your friend will be assigned to the next available agent and tagged so our team can reassign if needed.',
  },
  {
    question: 'What if my referral credit expires before I can use it?',
    answer:
      'Expired credits cannot be reinstated. We recommend planning your next trip ahead of expiry.',
  },
];

export const REFERRAL_TERMS_SUMMARY =
  'Referral statuses: Referral submitted — your friend signed up and we are reviewing eligibility. Contacted — our team has reached out. In progress — they are actively working on trip options. Booked — they completed checkout and received their credit. Success — you received your $200 after their first departure. Ineligible — automatic checks determined they do not qualify. Expired — they did not book within 90 days. Rejected — they did not purchase and the lead was closed. By participating you agree to program rules described in the FAQ and communications from your BCC agent.';
